import { Component, OnInit } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';
import { ConferenceEligibilityService } from 'src/app/core/services/conference-eligibility.service';
import { ProfileService } from 'src/app/core/services/profile.service';
import { UsersService } from 'src/app/core/services/users.service';
import {
  ConferenceEligibilityConfigurationModel,
  ConferenceEligibilityLevelRuleModel,
  ConferenceEligibilityRoleCategoryModel,
  ConferenceEligibilityRoleCategoryUserModel,
  ConferenceOpportunityQuestionModel,
  ConferenceOpportunityModel
} from 'src/app/models/conferenceEligibilityModels';
import { AlertService } from 'src/app/shared/services/alert.service';

@Component({
  selector: 'app-conference-eligibility-management',
  templateUrl: './conference-eligibility-management.component.html',
  standalone: false
})
export class ConferenceEligibilityManagementComponent extends BaseComponent implements OnInit {
  configuration: ConferenceEligibilityConfigurationModel;
  opportunities: ConferenceOpportunityModel[] = [];
  jobTitleOptions: SelectItem[] = [];
  employeeOptions: SelectItem[] = [];
  overrideUserID: number;
  overrideRoleCategoryID: number;

  loadingConfiguration = false;
  loadingOpportunities = false;
  savingConfiguration = false;
  savingOpportunity = false;
  savingOverride = false;

  openRoleCategory = false;
  openOpportunity = false;
  selectedRoleCategory: ConferenceEligibilityRoleCategoryModel;
  selectedOpportunity: ConferenceOpportunityModel;

  constructor(
    private conferenceEligibilityService: ConferenceEligibilityService,
    private profileService: ProfileService,
    private usersService: UsersService,
    private alertService: AlertService,
  ) {
    super();
  }

  ngOnInit() {
    this.loadConfiguration();
    this.loadOpportunities();
    this.subs.push(
      this.profileService.getJobTitles().subscribe(jobTitles => {
        this.jobTitleOptions = jobTitles
          .filter(jobTitle => !jobTitle.archived)
          .map(jobTitle => ({ label: jobTitle.jobTitle, value: jobTitle.id }));
      })
    );
    this.subs.push(
      this.usersService.getUsers().subscribe(users => {
        this.employeeOptions = users
          .filter(user => !user.inactive)
          .map(user => ({
            label: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            value: user.id
          }))
          .filter(option => !!option.value)
          .sort((left, right) => left.label.localeCompare(right.label));
      })
    );
  }

  loadConfiguration() {
    this.loadingConfiguration = true;
    this.subs.push(
      this.conferenceEligibilityService.getConfiguration().subscribe({
        next: configuration => {
          this.configuration = this.normalizeConfiguration(configuration);
          this.loadingConfiguration = false;
        },
        error: error => {
          this.alertService.error(error?.error || 'Unable to load conference eligibility configuration.');
          this.loadingConfiguration = false;
        }
      })
    );
  }

  loadOpportunities() {
    this.loadingOpportunities = true;
    this.subs.push(
      this.conferenceEligibilityService.getOpportunities().subscribe({
        next: opportunities => {
          this.opportunities = opportunities.map(opportunity => this.normalizeOpportunity(opportunity));
          this.loadingOpportunities = false;
        },
        error: error => {
          this.alertService.error(error?.error || 'Unable to load conference opportunities.');
          this.loadingOpportunities = false;
        }
      })
    );
  }

  saveConfiguration() {
    if (!this.configuration) {
      return;
    }

    this.savingConfiguration = true;
    const payload = this.createConfigurationPayload(this.configuration);
    this.subs.push(
      this.conferenceEligibilityService.saveConfiguration(payload).subscribe({
        next: configuration => {
          this.configuration = this.normalizeConfiguration(configuration);
          this.savingConfiguration = false;
          this.alertService.success('Conference eligibility configuration saved.');
        },
        error: error => {
          this.alertService.error(this.getErrorMessage(error, 'Unable to save conference eligibility configuration.'));
          this.savingConfiguration = false;
        }
      })
    );
  }

  newRoleCategory() {
    this.selectedRoleCategory = {
      name: '',
      description: '',
      appliesFileReviewRule: false,
      archived: false,
      jobTitleIDs: [],
      levelRules: this.createDefaultLevelRules()
    };
    this.openRoleCategory = true;
  }

  editRoleCategory(roleCategory: ConferenceEligibilityRoleCategoryModel) {
    this.selectedRoleCategory = {
      ...roleCategory,
      jobTitleIDs: [...(roleCategory.jobTitleIDs || [])],
      levelRules: this.createLevelRules(roleCategory.levelRules)
    };
    this.openRoleCategory = true;
  }

  saveRoleCategory() {
    if (!this.selectedRoleCategory || !this.selectedRoleCategory.name) {
      this.alertService.error('Role category name is required.');
      return;
    }

    const roleCategoryId = this.getEntityId(this.selectedRoleCategory);
    const conflictingJobTitleIDs = this.toNumberArray(this.selectedRoleCategory.jobTitleIDs).filter(jobTitleID =>
      (this.configuration?.roleCategories || []).some(roleCategory =>
        this.getEntityId(roleCategory) !== roleCategoryId
        && (roleCategory.jobTitleIDs || []).includes(jobTitleID)));
    if (conflictingJobTitleIDs.length) {
      this.alertService.error(`Each job title can belong to only one role category. Already assigned: ${this.getJobTitleLabels(conflictingJobTitleIDs)}.`);
      return;
    }

    this.saveRoleCategoryPayload(this.selectedRoleCategory, true, 'Conference eligibility configuration saved.');
  }

  getRoleCategoryJobTitleOptions() {
    const roleCategoryId = this.getEntityId(this.selectedRoleCategory);
    const assignedElsewhere = new Set<number>();
    (this.configuration?.roleCategories || []).forEach(roleCategory => {
      if (this.getEntityId(roleCategory) !== roleCategoryId) {
        (roleCategory.jobTitleIDs || []).forEach(jobTitleID => assignedElsewhere.add(jobTitleID));
      }
    });

    return this.jobTitleOptions.map(option => ({
      ...option,
      disabled: assignedElsewhere.has(Number(option.value))
    }));
  }

  saveUserOverride() {
    if (!this.overrideUserID || !this.overrideRoleCategoryID) {
      this.alertService.error('Select an employee and a role category for the individual override.');
      return;
    }

    this.savingOverride = true;
    const payload: ConferenceEligibilityRoleCategoryUserModel = {
      userID: Number(this.overrideUserID),
      roleCategoryID: Number(this.overrideRoleCategoryID)
    };
    this.subs.push(
      this.conferenceEligibilityService.saveRoleCategoryUserOverride(payload).subscribe({
        next: configuration => {
          this.configuration = this.normalizeConfiguration(configuration);
          this.overrideUserID = null;
          this.overrideRoleCategoryID = null;
          this.savingOverride = false;
          this.alertService.success('Individual role category override saved.');
        },
        error: error => {
          this.alertService.error(this.getErrorMessage(error, 'Unable to save the individual role category override.'));
          this.savingOverride = false;
        }
      })
    );
  }

  removeUserOverride(override: ConferenceEligibilityRoleCategoryUserModel) {
    const overrideId = this.getEntityId(override);
    if (!overrideId) {
      return;
    }

    this.savingOverride = true;
    this.subs.push(
      this.conferenceEligibilityService.deleteRoleCategoryUserOverride(overrideId).subscribe({
        next: configuration => {
          this.configuration = this.normalizeConfiguration(configuration);
          this.savingOverride = false;
          this.alertService.success('Individual override removed. The employee now uses their job-title category.');
        },
        error: error => {
          this.alertService.error(this.getErrorMessage(error, 'Unable to remove the individual role category override.'));
          this.savingOverride = false;
        }
      })
    );
  }

  archiveRoleCategory(roleCategory: ConferenceEligibilityRoleCategoryModel) {
    const roleCategoryId = this.getEntityId(roleCategory);
    if (!roleCategoryId) {
      this.alertService.error('Unable to archive role category before it has been saved.');
      return;
    }

    this.saveRoleCategoryPayload({ ...roleCategory, id: roleCategoryId, archived: true }, false, 'Role category archived.');
  }

  newOpportunity() {
    this.selectedOpportunity = {
      title: '',
      location: '',
      levelID: this.configuration?.levels?.[0]?.value || 1,
      startDate: this.formatDate(new Date()),
      endDate: this.formatDate(new Date()),
      applicationDeadline: null,
      description: '',
      statusID: this.configuration?.opportunityStatuses?.[0]?.value || 1,
      maxRecommendedParticipants: null,
      archived: false,
      questions: []
    };
    this.openOpportunity = true;
  }

  editOpportunity(opportunity: ConferenceOpportunityModel) {
    this.selectedOpportunity = { ...opportunity };
    this.openOpportunity = true;
  }

  saveOpportunity() {
    if (!this.selectedOpportunity || !this.selectedOpportunity.title) {
      this.alertService.error('Conference title is required.');
      return;
    }

    const payload = this.createOpportunityPayload(this.selectedOpportunity);
    if (!payload.levelID) {
      this.alertService.error('Conference level is required.');
      return;
    }

    if (!payload.statusID) {
      this.alertService.error('Conference status is required.');
      return;
    }

    if (payload.endDate < payload.startDate) {
      this.alertService.error('End date must be on or after the start date.');
      return;
    }

    const invalidQuestionIndex = (payload.questions || []).findIndex(question =>
      !question.prompt?.trim()
      || (this.questionRequiresOptions(question)
        && (question.options || []).filter(option => !!option?.trim()).length < 2));
    if (invalidQuestionIndex >= 0) {
      this.alertService.error(`Question ${invalidQuestionIndex + 1} needs a prompt and at least two choices when applicable.`);
      return;
    }

    this.savingOpportunity = true;
    this.subs.push(
      this.conferenceEligibilityService.saveOpportunity(payload).subscribe({
        next: opportunity => {
          const normalized = this.normalizeOpportunity(opportunity);
          const normalizedId = this.getEntityId(normalized);
          const existingIndex = this.opportunities.findIndex(x => this.getEntityId(x) === normalizedId);
          if (existingIndex >= 0) {
            this.opportunities[existingIndex] = normalized;
          } else {
            this.opportunities = [normalized, ...this.opportunities];
          }

          this.savingOpportunity = false;
          this.openOpportunity = false;
          this.alertService.success('Conference opportunity saved.');
        },
        error: error => {
          this.alertService.error(this.getErrorMessage(error, 'Unable to save conference opportunity.'));
          this.savingOpportunity = false;
        }
      })
    );
  }

  archiveOpportunity(opportunity: ConferenceOpportunityModel) {
    const opportunityId = this.getEntityId(opportunity);
    if (!opportunityId) {
      return;
    }

    this.subs.push(
      this.conferenceEligibilityService.archiveOpportunity(opportunityId).subscribe({
        next: () => {
          this.opportunities = this.opportunities.filter(x => this.getEntityId(x) !== opportunityId);
          this.alertService.success('Conference opportunity archived.');
        },
        error: error => this.alertService.error(this.getErrorMessage(error, 'Unable to archive conference opportunity.'))
      })
    );
  }

  getJobTitleLabels(jobTitleIDs: number[]) {
    const labels = (jobTitleIDs || [])
      .map(jobTitleID => this.jobTitleOptions.find(option => option.value === jobTitleID)?.label)
      .filter(label => !!label);

    return labels.length ? labels.join(', ') : '-';
  }

  getLevelRulesSummary(roleCategory: ConferenceEligibilityRoleCategoryModel) {
    return (roleCategory.levelRules || [])
      .filter(rule => rule.isEligible)
      .map(rule => `${rule.levelLabel || this.getLevelLabel(rule.levelID)}: ${rule.requiredHours} hrs`)
      .join(', ') || 'No eligible levels';
  }

  getLevelLabel(levelID: number) {
    return this.configuration?.levels?.find(level => level.value === levelID)?.label || '';
  }

  getOpportunityStatusLabel(statusID: number) {
    return this.configuration?.opportunityStatuses?.find(status => status.value === statusID)?.label || '';
  }

  addQuestion() {
    this.selectedOpportunity.questions = [
      ...(this.selectedOpportunity.questions || []),
      {
        prompt: '',
        helpText: '',
        questionTypeID: 1,
        isRequired: false,
        sortOrder: this.selectedOpportunity.questions?.length || 0,
        options: []
      }
    ];
  }

  removeQuestion(index: number) {
    this.selectedOpportunity.questions.splice(index, 1);
    this.updateQuestionOrder();
  }

  moveQuestion(index: number, direction: number) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= this.selectedOpportunity.questions.length) {
      return;
    }

    const questions = this.selectedOpportunity.questions;
    [questions[index], questions[targetIndex]] = [questions[targetIndex], questions[index]];
    this.updateQuestionOrder();
  }

  addQuestionOption(question: ConferenceOpportunityQuestionModel) {
    question.options = [...(question.options || []), ''];
  }

  removeQuestionOption(question: ConferenceOpportunityQuestionModel, optionIndex: number) {
    question.options.splice(optionIndex, 1);
  }

  questionRequiresOptions(question: ConferenceOpportunityQuestionModel) {
    return question.questionTypeID === 5 || question.questionTypeID === 6;
  }

  onQuestionTypeChanged(question: ConferenceOpportunityQuestionModel) {
    if (this.questionRequiresOptions(question) && (!question.options || question.options.length < 2)) {
      question.options = ['', ''];
    }
    if (!this.questionRequiresOptions(question)) {
      question.options = [];
    }
  }

  private normalizeConfiguration(configuration: ConferenceEligibilityConfigurationModel): ConferenceEligibilityConfigurationModel {
    return {
      ...configuration,
      settings: {
        ...configuration.settings,
        id: this.getEntityId(configuration.settings),
        accrualStartDate: this.toDateInputValue(configuration.settings.accrualStartDate)
      },
      roleCategories: (configuration.roleCategories || [])
      .filter(roleCategory => !roleCategory.archived)
      .map(roleCategory => ({
        ...roleCategory,
        id: this.getEntityId(roleCategory),
        jobTitleIDs: this.toNumberArray(roleCategory.jobTitleIDs),
        levelRules: this.createLevelRules(roleCategory.levelRules, configuration.levels)
      })),
      userOverrides: (configuration.userOverrides || []).map(override => ({
        ...override,
        id: this.getEntityId(override),
        roleCategoryID: this.getNumberProperty(override, 'roleCategoryID', 'roleCategoryId', 'RoleCategoryID'),
        userID: this.getNumberProperty(override, 'userID', 'userId', 'UserID')
      }))
    };
  }

  private normalizeOpportunity(opportunity: ConferenceOpportunityModel): ConferenceOpportunityModel {
    return {
      ...opportunity,
      id: this.getEntityId(opportunity),
      levelID: this.getNumberProperty(opportunity, 'levelID', 'levelId', 'LevelID'),
      statusID: this.getNumberProperty(opportunity, 'statusID', 'statusId', 'StatusID'),
      startDate: this.toDateInputValue(opportunity.startDate),
      endDate: this.toDateInputValue(opportunity.endDate),
      applicationDeadline: opportunity.applicationDeadline ? this.toDateInputValue(opportunity.applicationDeadline) : null,
      questions: (opportunity.questions || []).map((question, index) => ({
        ...question,
        id: this.getEntityId(question),
        questionTypeID: this.getNumberProperty(question, 'questionTypeID', 'questionTypeId', 'QuestionTypeID'),
        sortOrder: index,
        options: [...(question.options || [])]
      }))
    };
  }

  private createConfigurationPayload(configuration: ConferenceEligibilityConfigurationModel): ConferenceEligibilityConfigurationModel {
    const settings = this.removeIdAliases(configuration.settings);
    return {
      ...configuration,
      settings: {
        ...settings,
        id: this.getEntityId(configuration.settings),
        accrualStartDate: this.toDateInputValue(configuration.settings.accrualStartDate),
        kraThreshold: this.toNumber(configuration.settings.kraThreshold),
        fileReviewThreshold: this.toNumber(configuration.settings.fileReviewThreshold),
        goodStandingLookbackMonths: this.toNumber(configuration.settings.goodStandingLookbackMonths),
        localNationalAdvanceDays: this.toNumber(configuration.settings.localNationalAdvanceDays),
        internationalAdvanceDays: this.toNumber(configuration.settings.internationalAdvanceDays)
      },
      roleCategories: []
    };
  }

  private saveRoleCategoryPayload(roleCategory: ConferenceEligibilityRoleCategoryModel, closeRoleCategoryDialog: boolean, successMessage: string) {
    this.savingConfiguration = true;
    const payload = this.createRoleCategoryPayload(roleCategory);
    this.subs.push(
      this.conferenceEligibilityService.saveRoleCategory(payload).subscribe({
        next: configuration => {
          this.configuration = this.normalizeConfiguration(configuration);
          this.savingConfiguration = false;
          if (closeRoleCategoryDialog) {
            this.openRoleCategory = false;
          }
          this.alertService.success(successMessage);
        },
        error: error => {
          this.alertService.error(this.getErrorMessage(error, 'Unable to save conference eligibility configuration.'));
          this.savingConfiguration = false;
        }
      })
    );
  }

  private createRoleCategoryPayload(roleCategory: ConferenceEligibilityRoleCategoryModel): ConferenceEligibilityRoleCategoryModel {
    const category = this.removeIdAliases(roleCategory);
    return {
      ...category,
      id: this.getEntityId(roleCategory),
      jobTitleIDs: this.toNumberArray(roleCategory.jobTitleIDs),
      levelRules: (roleCategory.levelRules || []).map(rule => ({
        ...this.removeIdAliases(rule),
        id: this.getEntityId(rule),
        roleCategoryID: this.getNumberProperty(rule, 'roleCategoryID', 'roleCategoryId', 'RoleCategoryID'),
        levelID: this.getNumberProperty(rule, 'levelID', 'levelId', 'LevelID'),
        requiredHours: this.toNumber(rule.requiredHours)
      }))
    };
  }

  private createOpportunityPayload(opportunity: ConferenceOpportunityModel): ConferenceOpportunityModel {
    const opportunityPayload = this.removeIdAliases(opportunity);
    return {
      ...opportunityPayload,
      id: this.getEntityId(opportunity),
      levelID: this.getNumberProperty(opportunity, 'levelID', 'levelId', 'LevelID'),
      statusID: this.getNumberProperty(opportunity, 'statusID', 'statusId', 'StatusID'),
      startDate: this.toDateInputValue(opportunity.startDate),
      endDate: this.toDateInputValue(opportunity.endDate),
      applicationDeadline: this.toNullableDateInputValue(opportunity.applicationDeadline),
      maxRecommendedParticipants: this.toNullableNumber(opportunity.maxRecommendedParticipants),
      questions: (opportunity.questions || []).map((question, index) => ({
        ...this.removeIdAliases(question),
        id: this.getEntityId(question),
        opportunityID: this.getNumberProperty(question, 'opportunityID', 'opportunityId', 'OpportunityID'),
        questionTypeID: this.getNumberProperty(question, 'questionTypeID', 'questionTypeId', 'QuestionTypeID'),
        sortOrder: index,
        options: (question.options || []).map(option => option?.trim()).filter(option => !!option)
      }))
    };
  }

  private createDefaultLevelRules(): ConferenceEligibilityLevelRuleModel[] {
    return this.createLevelRules([]);
  }

  private createLevelRules(levelRules: ConferenceEligibilityLevelRuleModel[], levelOptions = this.configuration?.levels || []): ConferenceEligibilityLevelRuleModel[] {
    const currentRules = levelRules || [];
    return levelOptions.map(level => {
      const rule = currentRules.find(x => this.getNumberProperty(x, 'levelID', 'levelId', 'LevelID') === level.value);
      return {
        id: this.getEntityId(rule),
        roleCategoryID: this.getNumberProperty(rule, 'roleCategoryID', 'roleCategoryId', 'RoleCategoryID'),
        levelID: level.value,
        levelLabel: level.label,
        isEligible: rule?.isEligible || false,
        requiredHours: this.toNumber(rule?.requiredHours)
      };
    });
  }

  private toDateInputValue(value: string) {
    if (!value) {
      return null;
    }

    return value.substring(0, 10);
  }

  private toNullableDateInputValue(value: string) {
    if (!value) {
      return null;
    }

    return this.toDateInputValue(value);
  }

  private toNumber(value: any) {
    const rawValue = value && typeof value === 'object' && value.value !== undefined
      ? value.value
      : value;
    const numberValue = Number(rawValue);
    return Number.isFinite(numberValue) ? numberValue : 0;
  }

  private toNullableNumber(value: any) {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numberValue = this.toNumber(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private toNumberArray(values: any[]) {
    return (values || [])
      .map(value => this.toNumber(value))
      .filter(value => value > 0);
  }

  private getEntityId(value: any) {
    return this.getNumberProperty(value, 'id', 'iD', 'ID');
  }

  private getNumberProperty(value: any, ...propertyNames: string[]) {
    return this.toNumber(this.getFirstDefinedPropertyValue(value, propertyNames));
  }

  private getFirstDefinedPropertyValue(value: any, propertyNames: string[]) {
    if (!value) {
      return undefined;
    }

    for (const propertyName of propertyNames) {
      if (value[propertyName] !== undefined && value[propertyName] !== null) {
        return value[propertyName];
      }
    }

    return undefined;
  }

  private removeIdAliases(value: any) {
    const result = { ...(value || {}) };
    delete result.iD;
    delete result.ID;
    delete result.roleCategoryId;
    delete result.RoleCategoryID;
    delete result.levelId;
    delete result.LevelID;
    delete result.statusId;
    delete result.StatusID;
    delete result.opportunityId;
    delete result.OpportunityID;
    delete result.questionTypeId;
    delete result.QuestionTypeID;
    return result;
  }

  private updateQuestionOrder() {
    this.selectedOpportunity.questions.forEach((question, index) => question.sortOrder = index);
  }

  private getErrorMessage(error: any, fallback: string) {
    if (typeof error?.error === 'string' && error.error) {
      return error.error;
    }

    if (typeof error?.error?.message === 'string' && error.error.message) {
      return error.error.message;
    }

    if (typeof error?.message === 'string' && error.message) {
      return error.message;
    }

    return fallback;
  }

  private formatDate(date: Date) {
    return date.toISOString().substring(0, 10);
  }
}
